/** Weather days and conditions, shared by the frame and its card preview. */
import { Icons } from "@/components/ui/icons";
import { parseLabel, parseTemp } from "./weather-content";

export interface WeatherDay {
  day: string;
  icon: string | null;
  high: number;
  low: number;
}

const MAX_VISIBLE_DAYS = 4;

function dayFrom(value: unknown): WeatherDay | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const day = typeof record.day === "string" ? record.day.trim() : "";
  if (!day) return null;
  // Agents send {day, high, low} per the canvas_frame guide; older
  // payloads used a single {day, temp}. Accept both.
  const high =
    parseTemp(record.high) ??
    parseTemp(record.temp) ??
    parseTemp(record.temperature) ??
    parseTemp(record.max);
  const low =
    parseTemp(record.low) ??
    parseTemp(record.min) ??
    parseTemp(record.temp) ??
    parseTemp(record.temperature);
  if (high === null || low === null) return null;
  const rawIcon = typeof record.icon === "string" ? record.icon : undefined;
  const condition = parseLabel(record.condition);
  const icon =
    rawIcon && rawIcon in Icons
      ? rawIcon
      : condition
        ? inferConditionIcon(condition)
        : null;
  return {
    day,
    icon,
    high: Math.max(high, low),
    low: Math.min(high, low),
  };
}

/** Up to four readable days of `content.days`. */
export function daysFrom(value: unknown): WeatherDay[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[])
    .map(dayFrom)
    .filter((day): day is WeatherDay => day !== null)
    .slice(0, MAX_VISIBLE_DAYS);
}

export function inferConditionIcon(condition: string): string {
  const text = condition.toLowerCase();
  if (/thunder|storm|lightning/.test(text)) return "Zap";
  if (/moon|night/.test(text)) return "Moon";
  if (/sun|clear/.test(text) && !/cloud/.test(text)) return "Sun";
  return "Cloud";
}
